/**
 * ``useOrderChat`` — realtime order chat for the storefront (user side).
 *
 * REST (``GET /orders/<id>/messages/``) loads history; the per-order WebSocket
 * (``/ws/orders/<id>/chat/``) streams new messages live — including seller (panel) and
 * bot-relayed messages, since every transport writes through the same service path.
 * Sending prefers the open socket and falls back to REST so a message is never lost to a
 * flaky connection. The server persists + broadcasts; we append the broadcast frame,
 * de-duplicating by id.
 */
import { useWebSocket, type WebSocketStatus } from "@melusine/shared-telegram";
import {
  orderChatWsPath,
  postOrderMessage,
  postOrderMessageWithAttachment,
  useOrderMessages,
  type OrderChatMessage,
} from "@entities/order";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  frameToMessage,
  isChatMessageFrame,
  type ChatMessageFrame,
  type OutgoingChatFrame,
} from "./frames";
import { mergeMessages } from "./lib";

export interface OrderChatState {
  readonly messages: readonly OrderChatMessage[];
  readonly isLoading: boolean;
  /** ``true`` while the live socket is connected. */
  readonly isLive: boolean;
  /** Raw connection status of the live socket (for a UI indicator). */
  readonly connectionStatus: WebSocketStatus;
  /** Send a user message; resolves once accepted (WS frame or REST 201). */
  readonly send: (text: string) => Promise<void>;
  /** Send a file (image/PDF) over multipart REST, optionally with text. */
  readonly sendAttachment: (file: File, text?: string) => Promise<void>;
  readonly isSending: boolean;
}

export function useOrderChat(orderId: number): OrderChatState {
  const history = useOrderMessages(orderId);

  const [live, setLive] = useState<readonly OrderChatMessage[]>([]);
  const seenIds = useRef<Set<number>>(new Set());
  const [isSending, setIsSending] = useState(false);

  // Reset the live overlay when switching orders or after a history reload.
  useEffect(() => {
    setLive([]);
    seenIds.current = new Set();
  }, [orderId, history.data]);

  const onMessage = useCallback((frame: { readonly type?: unknown }) => {
    if (!isChatMessageFrame(frame) || seenIds.current.has(frame.id)) {
      return;
    }
    seenIds.current.add(frame.id);
    setLive((prev) => [...prev, frameToMessage(frame)]);
  }, []);

  const socket = useWebSocket<ChatMessageFrame, OutgoingChatFrame>(
    orderChatWsPath(orderId),
    { onMessage },
  );

  const messages = useMemo<readonly OrderChatMessage[]>(
    () => mergeMessages(history.data ?? [], live),
    [history.data, live],
  );

  const send = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return;
      }
      setIsSending(true);
      try {
        // Prefer the socket; it persists + broadcasts via the same service path.
        if (socket.status === "open" && socket.send({ text: trimmed })) {
          return;
        }
        // Fallback: REST. The 201 body is the persisted message; append it (a later WS
        // frame for the same id is de-duplicated).
        const created = await postOrderMessage(orderId, trimmed);
        if (!seenIds.current.has(created.id)) {
          seenIds.current.add(created.id);
          setLive((prev) => [...prev, created]);
        }
      } finally {
        setIsSending(false);
      }
    },
    [orderId, socket],
  );

  const sendAttachment = useCallback(
    async (file: File, text = ""): Promise<void> => {
      setIsSending(true);
      try {
        // Files always go over REST (the socket is text-only). The 201 body is the
        // persisted message with its built attachment_url; append + de-dup by id.
        const created = await postOrderMessageWithAttachment(orderId, file, text);
        if (!seenIds.current.has(created.id)) {
          seenIds.current.add(created.id);
          setLive((prev) => [...prev, created]);
        }
      } finally {
        setIsSending(false);
      }
    },
    [orderId],
  );

  return {
    messages,
    isLoading: history.isLoading,
    isLive: socket.status === "open",
    connectionStatus: socket.status,
    send,
    sendAttachment,
    isSending,
  };
}
